import {
  getVoicePlatformSocketUrl,
  getVoicePlatformApiBaseUrl,
  isVoicePlatformRuntimeConfigured
} from '../hooks/voice/infrastructure/config/voiceEnvConfig';
import {
  resolveCallProvider,
  isWhatsappCallsUiEnabledForProvider
} from '../hooks/calls/resolveCallProvider';
import {
  isVoiceSocketDebugEnabled,
  voiceSocketLog,
  VOICE_SOCKET_DEBUG_GLOBAL_KEY,
  getAppEnvironment
} from './voiceSocketDebug';

/**
 * @param {string} url
 * @returns {string}
 */
function maskSocketUrl(url) {
  if (!url) return '(vacío)';
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch {
    return '(url inválida)';
  }
}

/**
 * Diagnóstico de permisos Voice/Fonema y socket.
 *
 * @param {Object|null|undefined} retailerInfo
 * @param {Object} [extras]
 */
export function logVoiceCallsDiagnostics(retailerInfo, extras = {}) {
  const voiceFlag = Boolean(retailerInfo?.voice_platform_calls_enabled);
  const fonemaFlag = Boolean(retailerInfo?.fonema_whatsapp_calls_enabled);
  const socketUrl = getVoicePlatformSocketUrl();
  const apiBaseUrl = getVoicePlatformApiBaseUrl();
  const provider = resolveCallProvider(retailerInfo);
  const callsUiEnabled = isWhatsappCallsUiEnabledForProvider(provider);
  const shouldStartVoiceRuntime = extras.shouldStartVoiceRuntime ?? null;

  const snapshot = {
    provider,
    callsUiEnabled,
    voice_platform_calls_enabled: voiceFlag,
    fonema_whatsapp_calls_enabled: fonemaFlag,
    voiceRuntimeConfigured: isVoicePlatformRuntimeConfigured(),
    socketUrlConfigured: Boolean(socketUrl),
    apiBaseUrlConfigured: Boolean(apiBaseUrl),
    socketUrl: maskSocketUrl(socketUrl),
    apiBaseUrl: maskSocketUrl(apiBaseUrl),
    voiceRoom: retailerInfo?.id != null ? String(retailerInfo.id) : '(sin retailerId)',
    voiceSocketDebug: isVoiceSocketDebugEnabled(),
    nodeEnv: getAppEnvironment(),
    debugGlobalKey: VOICE_SOCKET_DEBUG_GLOBAL_KEY,
    showHelpCenter: extras.showHelpCenter ?? null,
    shouldMountCallManager: extras.shouldMountCallManager ?? null,
    shouldStartVoiceRuntime,
    can_make_calls: extras.canMakeCalls ?? null,
    can_receive_calls: extras.canReceiveCalls ?? null,
    retailerUserId: extras.retailerUserId ?? null
  };

  voiceSocketLog('diagnóstico llamadas', snapshot);

  if (provider === 'voice' && shouldStartVoiceRuntime === false) {
    // eslint-disable-next-line no-console
    console.warn(
      '[VoiceSocket] runtime NO se monta — no verás connect/WebRTC.',
      {
        showHelpCenter: extras.showHelpCenter,
        shouldMountCallManager: extras.shouldMountCallManager,
        can_make_calls: extras.canMakeCalls,
        can_receive_calls: extras.canReceiveCalls
      }
    );
  }

  if (voiceFlag && !isVoicePlatformRuntimeConfigured()) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Voice] voice_platform_calls_enabled pero falta configurar el microservicio: '
      + 'ENV.VOICE_PLATFORM_API_URL (ej. http://localhost:4001).'
    );
  }

  if (provider === 'voice' && !isVoiceSocketDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.log(
      `[Voice] Provider voice activo. Logs de socket/WebRTC: window.enableMercatelyVoiceDebug()`
    );
  }

  return snapshot;
}

let diagnosticsLoggedForRetailer = null;

/**
 * @param {Object|null|undefined} retailerInfo
 * @param {Object} [extras]
 */
export function logVoiceCallsDiagnosticsOnce(retailerInfo, extras = {}) {
  const retailerId = retailerInfo?.id ?? 'unknown';
  const mountKey = `${retailerId}:${extras.shouldStartVoiceRuntime ? 'on' : 'off'}`;
  if (diagnosticsLoggedForRetailer === mountKey) return undefined;
  diagnosticsLoggedForRetailer = mountKey;
  return logVoiceCallsDiagnostics(retailerInfo, extras);
}

/**
 * @param {Object|null|undefined} retailerInfo
 */
export function registerVoiceCallsDiagnosticsOnWindow(retailerInfo) {
  if (typeof window === 'undefined') return;
  window.__MERCATELY_RETAILER_INFO__ = retailerInfo;
  window.explainVoiceCallsSetup = () => logVoiceCallsDiagnostics(
    window.__MERCATELY_RETAILER_INFO__
  );
}
